import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { stockApi } from '../api/stock-api';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { stockUnitLabels, stockUsageLabels } from '../domain/fulfillment';
import {
  isBelowMinimum,
  movementReasonLabels,
  stockCommandLabels,
  stockTotals,
  type StockCommandKind,
} from '../domain/stock';
import { MovementList } from '../features/stock/MovementList';
import { StockCommandDialog } from '../features/stock/StockCommandDialog';
import { StockItemDrawer } from '../features/stock/StockItemDrawer';
import { useStockReferences } from '../features/stock/use-stock-references';

const commands: StockCommandKind[] = ['receive', 'transfer', 'return', 'adjust', 'openingBalance'];

/** One stock item: balances by location, its ledger and the stock commands. */
export function StockItemPage() {
  const { stockItemId = '' } = useParams();
  const references = useStockReferences();
  const [command, setCommand] = useState<{ kind: StockCommandKind; locationId?: string } | null>(
    null,
  );
  const [editing, setEditing] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  if (references.loading) return <PageLoadingState label="Loading stock item…" />;
  if (references.error) {
    return (
      <ErrorState
        title="Stock could not be loaded"
        rpcName="ListStockItems"
        onRetry={references.refetch}
      />
    );
  }
  const item = references.item(stockItemId);
  if (!item) {
    return (
      <div className="empty-state">
        <strong>This stock item does not exist.</strong>
        <Link to="/stock">Back to stock</Link>
      </div>
    );
  }
  const unit = stockUnitLabels[item.stockUnit];
  const totals = stockTotals(references.balances, item.id);
  const low = isBelowMinimum(item, totals.onHand);
  const photo = stockApi.stockItemImageUrl(item);
  const balances = references.balances
    .filter((b) => b.stockItemId === item.id && (b.onHand !== 0 || b.allocated !== 0))
    .sort((a, b) =>
      references.locationName(a.locationId).localeCompare(references.locationName(b.locationId)),
    );

  return (
    <>
      <PageHeader
        backLink={<Link to="/stock">← Stock</Link>}
        title={item.displayName}
        titleAction={
          !item.active || low ? (
            <span className="stock-card-badges">
              {low ? <span className="state-badge stock-low">Below minimum</span> : null}
              {!item.active ? (
                <span className="state-badge state-badge-muted">Deactivated</span>
              ) : null}
            </span>
          ) : undefined
        }
        description={[
          item.sku,
          item.barcodes.length ? `barcode ${item.barcodes.join(', ')}` : '',
          item.usage ? stockUsageLabels[item.usage] : '',
          `counted in ${unit}`,
          `${totals.onHand} on hand, ${totals.allocated} reserved, ${totals.available} available`,
          item.minimumQuantity > 0 ? `minimum ${item.minimumQuantity} ${unit}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {commands.map((kind) => (
              <button
                key={kind}
                type="button"
                className={kind === 'receive' ? 'primary' : undefined}
                onClick={() => setCommand({ kind })}
                disabled={
                  !item.active &&
                  (kind === 'receive' || kind === 'return' || kind === 'openingBalance')
                }
              >
                {stockCommandLabels[kind]}
              </button>
            ))}
            <button type="button" onClick={() => setEditing(true)}>
              Edit
            </button>
          </>
        }
      />
      {editing ? <StockItemDrawer item={item} onClose={() => setEditing(false)} /> : null}
      {command ? (
        <StockCommandDialog
          kind={command.kind}
          item={item}
          references={references}
          defaultLocationId={command.locationId}
          onClose={() => setCommand(null)}
          onDone={(result) => {
            setCommand(null);
            setDone(
              `${result.replayed ? 'Already recorded' : movementReasonLabels[result.movement.reason]}: ${result.movement.quantity} ${unit}.`,
            );
          }}
        />
      ) : null}
      {photo || item.description ? (
        <div className="stock-item-about">
          {photo ? (
            <img src={photo} alt={`Photo of ${item.displayName}`} className="stock-item-photo" />
          ) : null}
          {item.description ? <p className="stock-item-description">{item.description}</p> : null}
        </div>
      ) : null}
      <div role="status" aria-live="polite" className="stock-status">
        {done ? <p className="scan-feedback">{done}</p> : null}
      </div>

      <section className="stock-section" aria-labelledby="balances-heading">
        <h2 id="balances-heading">Where it is</h2>
        {balances.length === 0 ? (
          <p className="empty-note">None in stock. Receive goods or record an opening balance.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Location</th>
                  <th scope="col">On hand</th>
                  <th scope="col">Reserved</th>
                  <th scope="col">Available</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {balances.map((balance) => (
                  <tr key={balance.locationId}>
                    <th scope="row">{references.locationName(balance.locationId)}</th>
                    <td>
                      {balance.onHand} {unit}
                    </td>
                    <td>
                      {balance.allocated} {unit}
                    </td>
                    <td>
                      {balance.available} {unit}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() =>
                          setCommand({ kind: 'transfer', locationId: balance.locationId })
                        }
                        aria-label={`Transfer from ${references.locationName(balance.locationId)}`}
                      >
                        Transfer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="stock-section" aria-labelledby="history-heading">
        <h2 id="history-heading">History</h2>
        <MovementList filters={{ stockItemId: item.id }} references={references} showItem={false} />
      </section>
    </>
  );
}
